import { z } from 'zod';
import type { Device } from '@/models';
import { getPublicSupabaseClient } from './supabase-client';

export interface DeviceRepository {
  list(): Promise<Device[]>;
  findBySlug(slug: string): Promise<Device | null>;
  listForProduct(productId: string): Promise<Device[]>;
  listCompatibleProductIds(deviceId: string): Promise<string[]>;
}

const rowSchema = z.object({
  id: z.string(),
  brand_id: z.string().nullable(),
  name_ar: z.string(),
  name_en: z.string().nullable(),
  slug: z.string(),
  image_url: z.string().nullable(),
  is_active: z.boolean().nullable(),
});

const COLUMNS = 'id, brand_id, name_ar, name_en, slug, image_url, is_active';

function toDevice(row: z.infer<typeof rowSchema>): Device {
  return {
    id: row.id,
    brandId: row.brand_id,
    nameAr: row.name_ar,
    nameEn: row.name_en,
    slug: row.slug,
    imageUrl: row.image_url,
    isActive: row.is_active ?? true,
  };
}

export const supabaseDeviceRepository: DeviceRepository = {
  async list() {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return [];
    }
    const { data, error } = await supabase
      .from('devices')
      .select(COLUMNS)
      .eq('is_active', true)
      .order('name_ar', { ascending: true });
    if (error || !data) {
      console.error('Failed to load devices:', error?.message);
      return [];
    }
    return data.flatMap((row) => {
      const parsed = rowSchema.safeParse(row);
      return parsed.success ? [toDevice(parsed.data)] : [];
    });
  },

  async findBySlug(slug) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return null;
    }
    const { data, error } = await supabase.from('devices').select(COLUMNS).eq('slug', slug).maybeSingle();
    if (error || !data) {
      return null;
    }
    const parsed = rowSchema.safeParse(data);
    return parsed.success ? toDevice(parsed.data) : null;
  },

  async listForProduct(productId) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return [];
    }
    const { data, error } = await supabase
      .from('product_compatibility')
      .select('device_id, devices(id, brand_id, name_ar, name_en, slug, image_url, is_active)')
      .eq('product_id', productId);

    if (error || !data) {
      return [];
    }
    return data.flatMap((row) => {
      const device = (row as { devices?: unknown }).devices;
      const parsed = rowSchema.safeParse(device);
      return parsed.success ? [toDevice(parsed.data)] : [];
    });
  },

  async listCompatibleProductIds(deviceId) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return [];
    }
    const { data, error } = await supabase
      .from('product_compatibility')
      .select('product_id')
      .eq('device_id', deviceId);
    if (error || !data) {
      return [];
    }
    return data.map((row) => (row as { product_id: string }).product_id);
  },
};
